import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/analytics-1.4-apis/analytics-1.4-apis/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "segmentation-reference"
    }}>{`Segmentation reference`}</h1>
    <p>{`Inline segmentation allows you to segment report data using segment definitions that are include as part of the `}<a parentName="p" {...{
        "href": "index.md"
      }}>{`reportDescription`}</a>{` :`}</p>
    <ul>
      <li parentName="ul">{`Segment by specific line items (only include data where page = "Home Page")`}</li>
      <li parentName="ul">{`Segment classified values by search criteria (only include data where a classification of eVar1 matches a search)`}</li>
    </ul>
    <p>{`Note the following limitations:`}</p>
    <ul>
      <li parentName="ul">{`Segments are based on a page view container.`}</li>
      <li parentName="ul">{`A maximum of 10,000 values are returned when you define an inline segment. If you encounter this limitation, either reduce the scope of the report or use a regular segment.`}</li>
      <li parentName="ul">{`Some reports are not supported by inline segments. A complete list is at the bottom of this article.`}</li>
      <li parentName="ul">{`Prop values cannot be used to define inline segments.`}</li>
    </ul>
    <h1 {...{
      "id": "json-object-reference"
    }}>{`JSON object reference`}</h1>
    <p>{`The following properties are supported within the `}<inlineCode parentName="p">{`segments`}</inlineCode>{` object:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Element`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td"><inlineCode parentName="strong">{`id`}</inlineCode></strong></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`string`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Specifies the existing saved segment ID that you want to apply to a search. `}<strong parentName="td">{`Important:`}</strong>{` In version 1.4, inline segments no longer use the "id" parameter to specify the element as in 1.3. If migrating code from version 1.3, move the element value that was previously in the "id" parameter to the "element" parameter.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td"><inlineCode parentName="strong">{`element`}</inlineCode></strong></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`string`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Specifies the element (dimension) on which you want to segment.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td"><inlineCode parentName="strong">{`search`}</inlineCode></strong></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`object[]`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`(Optional) Applies a search to the element. This object includes the following values:`}<br /><inlineCode parentName="td">{`type`}</inlineCode>{`: A string that indicates the type of search to use. Valid values include `}<inlineCode parentName="td">{`and`}</inlineCode>{`, `}<inlineCode parentName="td">{`or`}</inlineCode>{`, and `}<inlineCode parentName="td">{`not`}</inlineCode>{`.`}<br /><inlineCode parentName="td">{`keywords`}</inlineCode>{`: A list of keywords to include or exclude from the search, based on the type. Keyword values can also leverage the following special characters to define advanced search criteria: `}<inlineCode parentName="td">{`*`}</inlineCode>{` Wild Card (e.g. "page*.html"), `}<inlineCode parentName="td">{`^`}</inlineCode>{` Starts With (e.g. "^http://"), and `}<inlineCode parentName="td">{`$`}</inlineCode>{` Ends With (e.g. ".html$").`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td"><inlineCode parentName="strong">{`classification`}</inlineCode></strong></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`string`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`(Optional, provide either a selected value, or a classification and a search value). Specifies how to integrate the include and an exclude segments.`}</td>
        </tr>
      </tbody>
    </table>
    <h2 {...{
      "id": "segment-by-line-items"
    }}>{`Segment by line items`}</h2>
    <p>{`Create a segment definition with an id of the reporting element you want to segment, with a selected value that contains the match criteria.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
   "reportDescription": {
       "reportSuiteID": "rsid",
       "date": "YYYY-09-01",
       "metrics": [
           { "id": "pageviews" }
       ],
       "elements": [
           { "id": "browser" }
       ],
       "segments": [
           { 
               "element": "page",
               "selected": ["Home Page", "Shopping Cart"]
           }
       ],
   }
}
`}</code></pre>
    <p>{`This report shows the page views for every browser when the page name is either "Home Page" or "Shopping Cart".`}</p>
    <h2 {...{
      "id": "segment-by-classification-value"
    }}>{`Segment by classification value`}</h2>
    <p>{`Create a segment definition with an id of the reporting element you want to segment, the classification you want to match, and a search string.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
   "reportDescription": {
       "reportSuiteID": "rsid",
       "date": "YYYY-09-01",
       "metrics": [
           { "id": "pageviews" }
       ],
       "elements": [
           { "id": "browser" }
       ],
       "segments": [
           { 
               "element": "eVar1",
               "classification": "Group Name",
               "search": { "type": "OR", "keywords": ["Administrator", "Manager", "Director"] }
           }
       ],
   }
}
`}</code></pre>
    <p>{`This report shows the page views for every browser when the "Group Name" classification of eVar1 contains "Administrator, "Manager", or "Director".`}</p>
    <h2 {...{
      "id": "multiple-segments"
    }}>{`Multiple Segments`}</h2>
    <p>{`You can provide multiple segments in the segments array. Segments are joined using AND. Segments cannot be nested.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`"segments": [
    { 
        "element": "page",
        "selected": ["Home Page", "Shopping Cart"]
    },
    { 
        "element": "eVar1",
        "classification": "Group Name",
        "search": { "type": "OR", "keywords": ["Administrator", "Manager", "Director"] }
    }
]
`}</code></pre>
    <p>{`Including this segment definition in a report shows metrics where the page name is either "Home Page" or "Shopping Cart", and the "Group Name" classification of eVar1 contains "Administrator, "Manager", or "Director".`}</p>
    <h2 {...{
      "id": "examples"
    }}>{`Examples`}</h2>
    <p>{`The following table contains an example of the different search types available with inline segmentation.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Classification Search Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Search Example`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Equals`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`"search": { "type": "AND", "keywords": ["^key$"] }`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Does not contain`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`"search": { "type": "NOT", "keywords": ["^key$"] }`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Contains`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`"search": { "type": "AND", "keywords": ["key word"] }`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Contains all of`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`"search": { "type": "AND", "keywords": ["key", "word"] }`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Contains at least one of`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`"search": { "type": "OR", "keywords": ["key", "word"] }`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Does not contain`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`"search": { "type": "NOT", "keywords": ["key"] }`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Is null`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`N/A. Empty classifications are not matched by a search.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Is not null`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Run a report on the selected element and classification.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Starts with`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`"search": { "type": "AND", "keywords": ["^key"] }`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Does not start with`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`"search": { "type": "NOT", "keywords": ["^key"] }`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Ends with`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`"search": { "type": "AND", "keywords": ["key$"] }`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Does not end with`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`"search": { "type": "NOT", "keywords": ["key$"] }`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <h2 {...{
      "id": "unsupported-reports-and-elements"
    }}>{`Unsupported reports and elements`}</h2>
    <p>{`Inline segments cannot be applied to the following report data:`}</p>
    <ul>
      <li parentName="ul">{`Pathing reports`}</li>
      <li parentName="ul">{`Mobile carrier`}</li>
      <li parentName="ul">{`Clickmap`}</li>
      <li parentName="ul">{`GeoSegmentation Region, City, and U.S. DMA Reports`}</li>
      <li parentName="ul">{`Hierarchies`}</li>
      <li parentName="ul">{`Clicks to page`}</li>
      <li parentName="ul">{`Time spent on page`}</li>
      <li parentName="ul">{`Time spent on site`}</li>
      <li parentName="ul">{`Visit depth`}</li>
      <li parentName="ul">{`Visit number`}</li>
      <li parentName="ul">{`Keyword URLs`}</li>
      <li parentName="ul">{`Return visits`}</li>
    </ul>
    <p>{`The following elements are not supported for inline segments.`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`pagedepth`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`visitnumber`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`mobilecarrier`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`hier`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`paths`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`fallout`}</inlineCode></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      